/**
 * Codebook — the 32 playtest-selection sentence codes from the LEXIKON
 * V1.21 rulebook materials, digitized into typed sentences with full
 * EN/IT/DE/FR translations and per-icon glossary.
 *
 * Source: LEXIKON_Codebook.pdf (Komunikon Language System, Selection).
 *
 * Token notation in physical play:
 *   - Frame color = grammar role (verb=red, noun=yellow, adj=blue,
 *     prep/conj=green, adverb=orange, neutral=grey)
 *   - Double border = plural
 *   - Tense bar under verb: dot-left=past, dot-centre=present, dot-right=future
 *   - X token = negation (or "select X-marked image" on dual-image cells)
 *   - "+" joins two icons into one word (e.g. Art+Person = Scientist)
 */

import type { CodeSentence, SentenceToken } from "../game/types";

function tok(
  partial: Omit<SentenceToken, "order"> & { order: number },
): SentenceToken {
  return partial;
}

function ref(key: string) {
  const m = /^([1-8])([A-D])([1-4])$/.exec(key);
  if (!m) throw new Error(`Bad ref ${key}`);
  return { board: Number(m[1]), col: m[2] as "A" | "B" | "C" | "D", row: Number(m[3]) as 1 | 2 | 3 | 4 };
}

export const CODES: CodeSentence[] = [
  {
    id: "01",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1A2"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2C4"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("7D1"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("7A1"), frameColor: "adj" }),
    ],
    translations: {
      en: "A child grows up into a happy adult",
      it: "Un figlio cresce in un adulto felice",
      de: "Ein Kind wächst zu einem glücklichen Erwachsenen heran",
      fr: "Un enfant grandit en devenant un adulte heureux",
    },
    glossary: [
      { iconKey: "1A2", pos: "noun", gloss: { en: "Child / Son", it: "Figlio", de: "Kind", fr: "Enfant" } },
      { iconKey: "2C4", pos: "verb", tense: "present", gloss: { en: "Grow / Improve", it: "Crescere", de: "Wachsen", fr: "Grandir" } },
      { iconKey: "7D1", pos: "noun", gloss: { en: "Adult / Mature", it: "Adulto", de: "Erwachsen", fr: "Adulte" } },
      { iconKey: "7A1", pos: "adj", gloss: { en: "Happy / Positive", it: "Felice", de: "Glücklich", fr: "Heureux" } },
    ],
  },
  {
    id: "02",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1A3"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("3A4"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("8C1"), frameColor: "adverb" }),
      tok({ order: 4, icon: ref("5A3"), frameColor: "adverb" }),
    ],
    translations: {
      en: "Friends play more today",
      it: "Gli amici giocano di più oggi",
      de: "Freunde spielen heute mehr",
      fr: "Des amis jouent davantage aujourd'hui",
    },
    glossary: [
      { iconKey: "1A3", pos: "noun", gloss: { en: "Friends", it: "Amici", de: "Freunde", fr: "Amis" } },
      { iconKey: "3A4", pos: "verb", tense: "present", gloss: { en: "Play", it: "Giocare", de: "Spielen", fr: "Jouer" } },
      { iconKey: "8C1", pos: "adverb", gloss: { en: "More", it: "Più", de: "Mehr", fr: "Davantage" } },
      { iconKey: "5A3", pos: "adverb", gloss: { en: "Today", it: "Oggi", de: "Heute", fr: "Aujourd'hui" } },
    ],
  },
  {
    id: "03",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("3D3"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("6C2"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("5B1"), frameColor: "adverb" }),
    ],
    translations: {
      en: "Space moves me at night",
      it: "Lo spazio mi emoziona di notte",
      de: "Der Weltraum bewegt mich nachts",
      fr: "L'espace m'émeut la nuit",
    },
    glossary: [
      { iconKey: "1A1", pos: "noun", gloss: { en: "I / Myself", it: "Io", de: "Ich", fr: "Moi" } },
      { iconKey: "3D3", pos: "verb", tense: "present", gloss: { en: "Feel emotion", it: "Emozionarsi", de: "Empfinden", fr: "Émouvoir" } },
      { iconKey: "6C2", pos: "noun", gloss: { en: "Space / Universe", it: "Spazio", de: "Weltraum", fr: "Espace" } },
      { iconKey: "5B1", pos: "adverb", gloss: { en: "Night", it: "Notte", de: "Nachts", fr: "Nuit" } },
    ],
  },
  {
    id: "04",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1A3"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2B4"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("1A4"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("7C1"), frameColor: "adj" }),
    ],
    translations: {
      en: "A friend helps a worried person",
      it: "Un amico aiuta una persona preoccupata",
      de: "Ein Freund hilft einer besorgten Person",
      fr: "Un ami aide une personne inquiète",
    },
    glossary: [
      { iconKey: "1A3", pos: "noun", gloss: { en: "Friend", it: "Amico", de: "Freund", fr: "Ami" } },
      { iconKey: "2B4", pos: "verb", tense: "present", gloss: { en: "Help / Support", it: "Aiutare", de: "Helfen", fr: "Aider" } },
      { iconKey: "1A4", pos: "noun", gloss: { en: "Person", it: "Persona", de: "Person", fr: "Personne" } },
      { iconKey: "7C1", pos: "adj", gloss: { en: "Worried / Stressed", it: "Preoccupata", de: "Besorgt", fr: "Inquiète" } },
    ],
  },
  {
    id: "05",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1A4"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("3D4"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("6B1"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("7A4"), frameColor: "adj" }),
    ],
    translations: {
      en: "A person builds a different home",
      it: "Una persona costruisce una casa diversa",
      de: "Eine Person baut ein anderes Haus",
      fr: "Une personne construit une maison différente",
    },
    glossary: [
      { iconKey: "1A4", pos: "noun", gloss: { en: "Person", it: "Persona", de: "Person", fr: "Personne" } },
      { iconKey: "3D4", pos: "verb", tense: "present", gloss: { en: "Build / Make", it: "Costruire", de: "Bauen", fr: "Construire" } },
      { iconKey: "6B1", pos: "noun", gloss: { en: "Home", it: "Casa", de: "Haus", fr: "Maison" } },
      { iconKey: "7A4", pos: "adj", gloss: { en: "Different", it: "Diversa", de: "Anders", fr: "Différente" } },
    ],
  },
  {
    id: "06",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("6A3"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("3D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("5A2"), frameColor: "adverb" }),
      tok({ order: 4, icon: ref("8A4"), frameColor: "adverb" }),
    ],
    translations: {
      en: "The world changes almost every week",
      it: "Il mondo cambia quasi ogni settimana",
      de: "Die Welt verändert sich fast jede Woche",
      fr: "Le monde change presque chaque semaine",
    },
    glossary: [
      { iconKey: "6A3", pos: "noun", gloss: { en: "World", it: "Mondo", de: "Welt", fr: "Monde" } },
      { iconKey: "3D1", pos: "verb", tense: "present", gloss: { en: "Transform / Change", it: "Cambiare", de: "Verändern", fr: "Changer" } },
      { iconKey: "5A2", pos: "adverb", gloss: { en: "Weekly", it: "Ogni settimana", de: "Wöchentlich", fr: "Chaque semaine" } },
      { iconKey: "8A4", pos: "adverb", gloss: { en: "Almost", it: "Quasi", de: "Fast", fr: "Presque" } },
    ],
  },
  {
    id: "07",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1A4"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("7D2"), frameColor: "adj" }),
      tok({ order: 3, icon: ref("2B1"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("5D4"), frameColor: "adverb" }),
    ],
    translations: {
      en: "A fat person always eats",
      it: "Una persona grassa mangia sempre",
      de: "Eine dicke Person isst immer",
      fr: "Une personne grosse mange toujours",
    },
    glossary: [
      { iconKey: "1A4", pos: "noun", gloss: { en: "Person", it: "Persona", de: "Person", fr: "Personne" } },
      { iconKey: "7D2", pos: "adj", gloss: { en: "Fat / Plump", it: "Grassa", de: "Dick", fr: "Grosse" } },
      { iconKey: "2B1", pos: "verb", tense: "present", gloss: { en: "Eat", it: "Mangiare", de: "Essen", fr: "Manger" } },
      { iconKey: "5D4", pos: "adverb", gloss: { en: "Always", it: "Sempre", de: "Immer", fr: "Toujours" } },
    ],
  },
  {
    id: "08",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("7D1"), frameColor: "noun", plural: true }),
      tok({ order: 2, icon: ref("3A4"), frameColor: "verb", tense: "past" }),
      tok({ order: 3, icon: ref("5B4"), frameColor: "adverb" }),
      tok({ order: 4, icon: ref("6D1"), frameColor: "noun" }),
    ],
    translations: {
      en: "The children played after school",
      it: "I bambini giocarono dopo la scuola",
      de: "Die Kinder spielten nach der Schule",
      fr: "Les enfants ont joué après l'école",
    },
    glossary: [
      { iconKey: "7D1", pos: "noun", plural: true, gloss: { en: "Children", it: "Bambini", de: "Kinder", fr: "Enfants" } },
      { iconKey: "3A4", pos: "verb", tense: "past", gloss: { en: "Play", it: "Giocare", de: "Spielen", fr: "Jouer" } },
      { iconKey: "5B4", pos: "adverb", gloss: { en: "After / Later", it: "Dopo", de: "Nach", fr: "Après" } },
      { iconKey: "6D1", pos: "noun", gloss: { en: "School", it: "Scuola", de: "Schule", fr: "École" } },
    ],
  },
  {
    id: "09",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("7D1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("3A2"), frameColor: "verb", tense: "past" }),
      tok({ order: 3, icon: ref("1C2"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("4B3"), frameColor: "noun" }),
    ],
    translations: {
      en: "The child hit her sibling in the eye",
      it: "Il bambino ha colpito il fratello all'occhio",
      de: "Das Kind schlug sein Geschwisterkind ins Auge",
      fr: "L'enfant a frappé son frère ou sœur dans l'œil",
    },
    glossary: [
      { iconKey: "7D1", pos: "noun", gloss: { en: "Child", it: "Bambino", de: "Kind", fr: "Enfant" } },
      { iconKey: "3A2", pos: "verb", tense: "past", gloss: { en: "Hit / Strike", it: "Colpire", de: "Schlagen", fr: "Frapper" } },
      { iconKey: "1C2", pos: "noun", gloss: { en: "Sibling", it: "Fratello", de: "Geschwisterkind", fr: "Frère / Sœur" } },
      { iconKey: "4B3", pos: "noun", gloss: { en: "Eye / Sight", it: "Occhio", de: "Auge", fr: "Œil" } },
    ],
  },
  {
    id: "10",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1C1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("3D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("4D4"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("5C2"), frameColor: "adverb" }),
    ],
    translations: {
      en: "You change the car yearly",
      it: "Cambi la macchina ogni anno",
      de: "Du wechselst jedes Jahr das Auto",
      fr: "Tu changes la voiture chaque année",
    },
    glossary: [
      { iconKey: "1C1", pos: "noun", gloss: { en: "You", it: "Tu", de: "Du", fr: "Tu" } },
      { iconKey: "3D1", pos: "verb", tense: "present", gloss: { en: "Change / Transform", it: "Cambiare", de: "Wechseln", fr: "Changer" } },
      { iconKey: "4D4", pos: "noun", gloss: { en: "Car / Transport", it: "Macchina", de: "Auto", fr: "Voiture" } },
      { iconKey: "5C2", pos: "adverb", gloss: { en: "Yearly", it: "Ogni anno", de: "Jährlich", fr: "Chaque année" } },
    ],
  },
  {
    id: "11",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("7A1"), frameColor: "adj" }),
      tok({ order: 2, icon: ref("1B3"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("5D4"), frameColor: "adverb", negation: true }),
      tok({ order: 4, icon: ref("2B4"), frameColor: "verb", tense: "present" }),
    ],
    translations: {
      en: "A happy wife never complains",
      it: "Una moglie felice non si lamenta mai",
      de: "Eine glückliche Frau beschwert sich nie",
      fr: "Une femme heureuse ne se plaint jamais",
    },
    glossary: [
      { iconKey: "7A1", pos: "adj", gloss: { en: "Happy / Positive", it: "Felice", de: "Glücklich", fr: "Heureuse" } },
      { iconKey: "1B3", pos: "noun", gloss: { en: "Partner / Wife", it: "Moglie", de: "Frau", fr: "Femme" } },
      { iconKey: "5D4", pos: "adverb", negation: true, gloss: { en: "Never", it: "Mai", de: "Nie", fr: "Jamais" } },
      { iconKey: "2B4", pos: "verb", tense: "present", gloss: { en: "Complain / Speak", it: "Lamentarsi", de: "Beschweren", fr: "Se plaindre" } },
    ],
  },
  {
    id: "12",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1A2"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2A1"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("7A3"), frameColor: "adj" }),
      tok({ order: 4, icon: ref("4B1"), frameColor: "noun" }),
    ],
    translations: {
      en: "I dream of the latest phone",
      it: "Sogno l'ultimo telefono",
      de: "Ich träume vom neuesten Telefon",
      fr: "Je rêve du dernier téléphone",
    },
    glossary: [
      { iconKey: "1A2", pos: "noun", gloss: { en: "I", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "2A1", pos: "verb", tense: "present", gloss: { en: "Dream / Think", it: "Sognare", de: "Träumen", fr: "Rêver" } },
      { iconKey: "7A3", pos: "adj", gloss: { en: "Latest / Last", it: "Ultimo", de: "Neueste", fr: "Dernier" } },
      { iconKey: "4B1", pos: "noun", gloss: { en: "Phone / Technology", it: "Telefono", de: "Telefon", fr: "Téléphone" } },
    ],
  },
  {
    id: "13",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1C4"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("5D4"), frameColor: "adverb" }),
      tok({ order: 3, icon: ref("2B4"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("1B4"), frameColor: "noun" }),
    ],
    translations: {
      en: "A leader always supports his people",
      it: "Un leader sostiene sempre il suo popolo",
      de: "Ein Anführer unterstützt immer sein Volk",
      fr: "Un leader soutient toujours son peuple",
    },
    glossary: [
      { iconKey: "1C4", pos: "noun", gloss: { en: "Leader", it: "Leader", de: "Anführer", fr: "Leader" } },
      { iconKey: "5D4", pos: "adverb", gloss: { en: "Always", it: "Sempre", de: "Immer", fr: "Toujours" } },
      { iconKey: "2B4", pos: "verb", tense: "present", gloss: { en: "Support / Help", it: "Sostenere", de: "Unterstützen", fr: "Soutenir" } },
      { iconKey: "1B4", pos: "noun", gloss: { en: "People / Community", it: "Popolo", de: "Volk", fr: "Peuple" } },
    ],
  },
  {
    id: "14",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1A2"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2A2"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("7B4"), frameColor: "adj" }),
      tok({ order: 4, icon: ref("2C4"), frameColor: "noun" }),
    ],
    translations: {
      en: "I have big goals",
      it: "Ho grandi obiettivi",
      de: "Ich habe große Ziele",
      fr: "J'ai de grands objectifs",
    },
    glossary: [
      { iconKey: "1A2", pos: "noun", gloss: { en: "I", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "2A2", pos: "verb", tense: "present", gloss: { en: "Have / Decide", it: "Avere", de: "Haben", fr: "Avoir" } },
      { iconKey: "7B4", pos: "adj", gloss: { en: "Big / Great", it: "Grandi", de: "Große", fr: "Grands" } },
      { iconKey: "2C4", pos: "noun", gloss: { en: "Goals / Growth", it: "Obiettivi", de: "Ziele", fr: "Objectifs" } },
    ],
  },
  {
    id: "15",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("1A3"), frameColor: "noun", plural: true }),
      tok({ order: 2, icon: ref("3C4"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("6A2"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("8B3"), frameColor: "adverb" }),
      tok({ order: 5, icon: ref("6B2"), frameColor: "noun" }),
    ],
    translations: {
      en: "Friends search for a path through the mountain",
      it: "Amici cercano una strada attraverso la montagna",
      de: "Freunde suchen einen Weg durch das Gebirge",
      fr: "Des amis cherchent un chemin à travers la montagne",
    },
    glossary: [
      { iconKey: "1A3", pos: "noun", plural: true, gloss: { en: "Friends", it: "Amici", de: "Freunde", fr: "Amis" } },
      { iconKey: "3C4", pos: "verb", tense: "present", gloss: { en: "Search / Look for", it: "Cercare", de: "Suchen", fr: "Chercher" } },
      { iconKey: "6A2", pos: "noun", gloss: { en: "Path / Road", it: "Strada", de: "Weg", fr: "Chemin" } },
      { iconKey: "8B3", pos: "adverb", gloss: { en: "Through / Across", it: "Attraverso", de: "Durch", fr: "À travers" } },
      { iconKey: "6B2", pos: "noun", gloss: { en: "Mountain", it: "Montagna", de: "Gebirge", fr: "Montagne" } },
    ],
  },
  {
    id: "16",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("3D2"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("1C1"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("8C1"), frameColor: "adverb" }),
      tok({ order: 5, icon: ref("4A2"), frameColor: "noun" }),
    ],
    translations: {
      en: "I ask you for more money",
      it: "Io chiedo a voi più soldi",
      de: "Ich bitte euch um mehr Geld",
      fr: "Je vous demande plus d'argent",
    },
    glossary: [
      { iconKey: "1A1", pos: "noun", gloss: { en: "I / Myself", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "3D2", pos: "verb", tense: "present", gloss: { en: "Ask / Request", it: "Chiedere", de: "Bitten", fr: "Demander" } },
      { iconKey: "1C1", pos: "noun", gloss: { en: "You / To you", it: "A voi", de: "Euch", fr: "Vous" } },
      { iconKey: "8C1", pos: "adverb", gloss: { en: "More / Greater", it: "Più", de: "Mehr", fr: "Plus" } },
      { iconKey: "4A2", pos: "noun", gloss: { en: "Money / Wealth", it: "Soldi", de: "Geld", fr: "Argent" } },
    ],
  },
  {
    id: "17",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("5A2"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("7D4"), frameColor: "adj", negation: true }),
      tok({ order: 3, icon: ref("8D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("3C4"), frameColor: "noun" }),
      tok({ order: 5, icon: ref("7B2"), frameColor: "adj" }),
    ],
    translations: {
      en: "A short week equals easy work",
      it: "Una settimana non lunga uguale a lavoro non difficile",
      de: "Eine kurze Woche bedeutet leichte Arbeit",
      fr: "Une semaine courte équivaut à un travail facile",
    },
    glossary: [
      { iconKey: "5A2", pos: "noun", gloss: { en: "Week / Period", it: "Settimana", de: "Woche", fr: "Semaine" } },
      { iconKey: "7D4", pos: "adj", negation: true, gloss: { en: "Short / Not long", it: "Non lunga", de: "Kurz", fr: "Courte" } },
      { iconKey: "8D1", pos: "verb", tense: "present", gloss: { en: "Equals", it: "Uguale", de: "Bedeutet", fr: "Équivaut" } },
      { iconKey: "3C4", pos: "noun", gloss: { en: "Work / Job", it: "Lavoro", de: "Arbeit", fr: "Travail" } },
      { iconKey: "7B2", pos: "adj", gloss: { en: "Easy / Peaceful", it: "Facile", de: "Leicht", fr: "Facile" } },
    ],
  },
  {
    id: "18",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("1B2"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2B2"), frameColor: "verb", tense: "past" }),
      tok({ order: 3, icon: ref("6B1"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("5A4"), frameColor: "adverb" }),
      tok({ order: 5, icon: ref("5B1"), frameColor: "noun" }),
    ],
    translations: {
      en: "The parent locked the house before night",
      it: "Il genitore ha chiuso la casa prima della notte",
      de: "Das Elternteil schloss das Haus vor der Nacht ab",
      fr: "Le parent a fermé la maison avant la nuit",
    },
    glossary: [
      { iconKey: "1B2", pos: "noun", gloss: { en: "Parent", it: "Genitore", de: "Elternteil", fr: "Parent" } },
      { iconKey: "2B2", pos: "verb", tense: "past", gloss: { en: "Lock / Close", it: "Chiudere", de: "Abschließen", fr: "Fermer" } },
      { iconKey: "6B1", pos: "noun", gloss: { en: "Home", it: "Casa", de: "Haus", fr: "Maison" } },
      { iconKey: "5A4", pos: "adverb", gloss: { en: "Before / Early", it: "Prima", de: "Vor", fr: "Avant" } },
      { iconKey: "5B1", pos: "noun", gloss: { en: "Night", it: "Notte", de: "Nacht", fr: "Nuit" } },
    ],
  },
  {
    id: "19",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("1B2"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2C3"), frameColor: "verb", tense: "past" }),
      tok({ order: 3, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("7C2"), frameColor: "adj", negation: true }),
      tok({ order: 5, icon: ref("4C4"), frameColor: "noun" }),
    ],
    translations: {
      en: "The grandparent gave me ugly clothes",
      it: "Il nonno mi ha dato dei vestiti brutti",
      de: "Die Großmutter gab mir hässliche Kleidung",
      fr: "Le grand-parent m'a donné des vêtements laids",
    },
    glossary: [
      { iconKey: "1B2", pos: "noun", gloss: { en: "Grandparent", it: "Nonno", de: "Großmutter", fr: "Grand-parent" } },
      { iconKey: "2C3", pos: "verb", tense: "past", gloss: { en: "Give / Offer", it: "Dare", de: "Geben", fr: "Donner" } },
      { iconKey: "1A1", pos: "noun", gloss: { en: "Me / Myself", it: "Mi", de: "Mir", fr: "Me" } },
      { iconKey: "7C2", pos: "adj", negation: true, gloss: { en: "Ugly", it: "Brutti", de: "Hässlich", fr: "Laids" } },
      { iconKey: "4C4", pos: "noun", gloss: { en: "Clothes", it: "Vestiti", de: "Kleidung", fr: "Vêtements" } },
    ],
  },
  {
    id: "20",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("1A4"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2D1"), frameColor: "verb", tense: "past" }),
      tok({ order: 3, icon: ref("1D4"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("8B3"), frameColor: "prep" }),
      tok({ order: 5, icon: ref("6A1"), frameColor: "noun" }),
    ],
    translations: {
      en: "The person dreamt of running through a forest",
      it: "La persona sognò di correre attraverso una foresta",
      de: "Die Person träumte davon, durch einen Wald zu laufen",
      fr: "La personne a rêvé de courir à travers une forêt",
    },
    glossary: [
      { iconKey: "1A4", pos: "noun", gloss: { en: "Person", it: "Persona", de: "Person", fr: "Personne" } },
      { iconKey: "2D1", pos: "verb", tense: "past", gloss: { en: "Dream", it: "Sognare", de: "Träumen", fr: "Rêver" } },
      { iconKey: "1D4", pos: "verb", tense: "present", gloss: { en: "Run", it: "Correre", de: "Laufen", fr: "Courir" } },
      { iconKey: "8B3", pos: "prep", gloss: { en: "Through / Across", it: "Attraverso", de: "Durch", fr: "À travers" } },
      { iconKey: "6A1", pos: "noun", gloss: { en: "Forest", it: "Foresta", de: "Wald", fr: "Forêt" } },
    ],
  },
  {
    id: "21",
    iconCount: 6,
    tokens: [
      tok({ order: 1, icon: ref("3B1"), frameColor: "noun", compoundWithNext: true }),
      tok({ order: 1, icon: ref("1A4"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2A1"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("5C4"), frameColor: "adverb" }),
      tok({ order: 4, icon: ref("8D2"), frameColor: "prep" }),
      tok({ order: 5, icon: ref("4B2"), frameColor: "noun", plural: true }),
    ],
    translations: {
      en: "The scientist thinks often about animals",
      it: "Lo scienziato pensa spesso agli animali",
      de: "Der Wissenschaftler denkt oft über Tiere nach",
      fr: "Le scientifique pense souvent aux animaux",
    },
    glossary: [
      { iconKey: "3B1", pos: "noun", compound: { en: "Scientist (Science+Person)", it: "Scienziato (Scienza+Persona)", de: "Wissenschaftler (Wissenschaft+Person)", fr: "Scientifique (Science+Personne)" }, gloss: { en: "Science", it: "Scienza", de: "Wissenschaft", fr: "Science" } },
      { iconKey: "1A4", pos: "noun", compound: { en: "Scientist (Science+Person)", it: "Scienziato (Scienza+Persona)", de: "Wissenschaftler (Wissenschaft+Person)", fr: "Scientifique (Science+Personne)" }, gloss: { en: "Person", it: "Persona", de: "Person", fr: "Personne" } },
      { iconKey: "2A1", pos: "verb", tense: "present", gloss: { en: "Think", it: "Pensare", de: "Denken", fr: "Penser" } },
      { iconKey: "5C4", pos: "adverb", gloss: { en: "Often", it: "Spesso", de: "Oft", fr: "Souvent" } },
      { iconKey: "8D2", pos: "prep", gloss: { en: "About / Regarding", it: "A / Riguardo a", de: "Über", fr: "À propos de" } },
      { iconKey: "4B2", pos: "noun", plural: true, gloss: { en: "Animals", it: "Animali", de: "Tiere", fr: "Animaux" } },
    ],
  },
  {
    id: "22",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2C3"), frameColor: "verb", tense: "past" }),
      tok({ order: 3, icon: ref("1C1"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("7C3"), frameColor: "adj" }),
      tok({ order: 5, icon: ref("4D2"), frameColor: "noun" }),
    ],
    translations: {
      en: "I gave you my whole heart",
      it: "Ti ho dato tutto il mio cuore",
      de: "Ich gab dir mein ganzes Herz",
      fr: "Je t'ai donné tout mon cœur",
    },
    glossary: [
      { iconKey: "1A1", pos: "noun", gloss: { en: "I / Myself", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "2C3", pos: "verb", tense: "past", gloss: { en: "Give / Offer", it: "Dare", de: "Geben", fr: "Donner" } },
      { iconKey: "1C1", pos: "noun", gloss: { en: "You", it: "Tu", de: "Dir", fr: "Te" } },
      { iconKey: "7C3", pos: "adj", gloss: { en: "Whole / Complete", it: "Tutto / Intero", de: "Ganzes", fr: "Tout / Entier" } },
      { iconKey: "4D2", pos: "noun", gloss: { en: "Heart / Body", it: "Cuore", de: "Herz", fr: "Cœur" } },
    ],
  },
  {
    id: "23",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2D3"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("3D1"), frameColor: "verb", tense: "future" }),
      tok({ order: 4, icon: ref("4A3"), frameColor: "adj", negation: true }),
      tok({ order: 5, icon: ref("4A4"), frameColor: "noun" }),
    ],
    translations: {
      en: "I wish to become not afraid of the dark",
      it: "Desidero diventare non spaventato dal buio",
      de: "Ich wünsche mir, keine Angst vor dem Dunkeln zu haben",
      fr: "Je souhaite ne plus avoir peur du noir",
    },
    glossary: [
      { iconKey: "1A1", pos: "noun", gloss: { en: "I / Myself", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "2D3", pos: "verb", tense: "present", gloss: { en: "Wish", it: "Desiderare", de: "Wünschen", fr: "Souhaiter" } },
      { iconKey: "3D1", pos: "verb", tense: "future", gloss: { en: "Become", it: "Diventare", de: "Werden", fr: "Devenir" } },
      { iconKey: "4A3", pos: "adj", negation: true, gloss: { en: "Not afraid / Brave", it: "Non spaventato", de: "Keine Angst", fr: "Pas effrayé" } },
      { iconKey: "4A4", pos: "noun", gloss: { en: "Darkness", it: "Buio", de: "Dunkeln", fr: "Noir / Obscurité" } },
    ],
  },
  {
    id: "24",
    iconCount: 6,
    tokens: [
      tok({ order: 1, icon: ref("1A4"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2B1"), frameColor: "verb", tense: "past" }),
      tok({ order: 3, icon: ref("7B4"), frameColor: "adverb" }),
      tok({ order: 4, icon: ref("4A1"), frameColor: "noun" }),
      tok({ order: 5, icon: ref("8A2"), frameColor: "prep" }),
      tok({ order: 6, icon: ref("2D2"), frameColor: "verb", tense: "future", negation: true }),
    ],
    translations: {
      en: "A person ate a lot of food but will not sleep",
      it: "Una persona ha mangiato molto cibo ma non dormirà",
      de: "Eine Person hat viel gegessen, wird aber nicht schlafen",
      fr: "Une personne a mangé beaucoup mais ne dormira pas",
    },
    glossary: [
      { iconKey: "1A4", pos: "noun", gloss: { en: "Person", it: "Persona", de: "Person", fr: "Personne" } },
      { iconKey: "2B1", pos: "verb", tense: "past", gloss: { en: "Eat", it: "Mangiare", de: "Essen", fr: "Manger" } },
      { iconKey: "7B4", pos: "adverb", gloss: { en: "A lot", it: "Molto", de: "Viel", fr: "Beaucoup" } },
      { iconKey: "4A1", pos: "noun", gloss: { en: "Food", it: "Cibo", de: "Essen", fr: "Nourriture" } },
      { iconKey: "8A2", pos: "prep", gloss: { en: "But", it: "Ma", de: "Aber", fr: "Mais" } },
      { iconKey: "2D2", pos: "verb", tense: "future", negation: true, gloss: { en: "Will not sleep", it: "Non dormirà", de: "Wird nicht schlafen", fr: "Ne dormira pas" } },
    ],
  },
  {
    id: "25",
    iconCount: 6,
    tokens: [
      tok({ order: 1, icon: ref("1C1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("3C2"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("2C2"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("4D4"), frameColor: "noun" }),
      tok({ order: 5, icon: ref("8A3"), frameColor: "prep" }),
      tok({ order: 6, icon: ref("6C1"), frameColor: "noun" }),
    ],
    translations: {
      en: "You can drive the car to the city",
      it: "Tu puoi guidare la macchina fino in città",
      de: "Du kannst das Auto in die Stadt fahren",
      fr: "Tu peux conduire la voiture jusqu'en ville",
    },
    glossary: [
      { iconKey: "1C1", pos: "noun", gloss: { en: "You", it: "Tu", de: "Du", fr: "Tu" } },
      { iconKey: "3C2", pos: "verb", tense: "present", gloss: { en: "Can", it: "Potere", de: "Können", fr: "Pouvoir" } },
      { iconKey: "2C2", pos: "verb", tense: "present", gloss: { en: "Drive", it: "Guidare", de: "Fahren", fr: "Conduire" } },
      { iconKey: "4D4", pos: "noun", gloss: { en: "Car", it: "Macchina", de: "Auto", fr: "Voiture" } },
      { iconKey: "8A3", pos: "prep", gloss: { en: "To / Towards", it: "Verso / In", de: "In", fr: "Vers / À" } },
      { iconKey: "6C1", pos: "noun", gloss: { en: "City", it: "Città", de: "Stadt", fr: "Ville" } },
    ],
  },
  {
    id: "26",
    iconCount: 6,
    tokens: [
      tok({ order: 1, icon: ref("1C3"), frameColor: "adj" }),
      tok({ order: 2, icon: ref("1D3"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("2B3"), frameColor: "verb", tense: "past" }),
      tok({ order: 4, icon: ref("3C4"), frameColor: "noun" }),
      tok({ order: 5, icon: ref("7B4"), frameColor: "adj" }),
      tok({ order: 6, icon: ref("6C1"), frameColor: "noun" }),
    ],
    translations: {
      en: "A young foreigner searched for work in the big city",
      it: "Un giovane straniero cercò lavoro nella grande città",
      de: "Ein junger Ausländer suchte Arbeit in der großen Stadt",
      fr: "Un jeune étranger a cherché du travail dans la grande ville",
    },
    glossary: [
      { iconKey: "1C3", pos: "adj", gloss: { en: "Young", it: "Giovane", de: "Jung", fr: "Jeune" } },
      { iconKey: "1D3", pos: "noun", gloss: { en: "Foreigner", it: "Straniero", de: "Ausländer", fr: "Étranger" } },
      { iconKey: "2B3", pos: "verb", tense: "past", gloss: { en: "Search", it: "Cercare", de: "Suchen", fr: "Chercher" } },
      { iconKey: "3C4", pos: "noun", gloss: { en: "Work / Job", it: "Lavoro", de: "Arbeit", fr: "Travail" } },
      { iconKey: "7B4", pos: "adj", gloss: { en: "Big / Great", it: "Grande", de: "Große", fr: "Grande" } },
      { iconKey: "6C1", pos: "noun", gloss: { en: "City", it: "Città", de: "Stadt", fr: "Ville" } },
    ],
  },
  {
    id: "27",
    iconCount: 6,
    tokens: [
      tok({ order: 1, icon: ref("6A3"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("3D1"), frameColor: "verb", tense: "past" }),
      tok({ order: 3, icon: ref("7A2"), frameColor: "adj" }),
      tok({ order: 4, icon: ref("5B4"), frameColor: "prep" }),
      tok({ order: 5, icon: ref("7A3"), frameColor: "adj" }),
      tok({ order: 6, icon: ref("3A3"), frameColor: "noun" }),
    ],
    translations: {
      en: "The world became cold after the last war",
      it: "Il mondo divenne freddo dopo l'ultima guerra",
      de: "Die Welt wurde nach dem letzten Krieg kalt",
      fr: "Le monde est devenu froid après la dernière guerre",
    },
    glossary: [
      { iconKey: "6A3", pos: "noun", gloss: { en: "World", it: "Mondo", de: "Welt", fr: "Monde" } },
      { iconKey: "3D1", pos: "verb", tense: "past", gloss: { en: "Become / Change", it: "Divenire", de: "Werden", fr: "Devenir" } },
      { iconKey: "7A2", pos: "adj", gloss: { en: "Cold / Cool", it: "Freddo", de: "Kalt", fr: "Froid" } },
      { iconKey: "5B4", pos: "prep", gloss: { en: "After / Later", it: "Dopo", de: "Nach", fr: "Après" } },
      { iconKey: "7A3", pos: "adj", gloss: { en: "Last / Ultimate", it: "Ultima", de: "Letzte", fr: "Dernière" } },
      { iconKey: "3A3", pos: "noun", gloss: { en: "War / Conflict", it: "Guerra", de: "Krieg", fr: "Guerre" } },
    ],
  },
  {
    id: "28",
    iconCount: 6,
    tokens: [
      tok({ order: 1, icon: ref("4B2"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2B3"), frameColor: "verb", tense: "past" }),
      tok({ order: 3, icon: ref("6A1"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("7D3"), frameColor: "adj" }),
      tok({ order: 5, icon: ref("6D3"), frameColor: "noun" }),
      tok({ order: 6, icon: ref("2D2"), frameColor: "verb", tense: "present" }),
    ],
    translations: {
      en: "The animal searched the forest for a good place to sleep",
      it: "L'animale cercò nella foresta un buon posto per dormire",
      de: "Das Tier suchte im Wald einen guten Schlafplatz",
      fr: "L'animal a cherché dans la forêt un bon endroit pour dormir",
    },
    glossary: [
      { iconKey: "4B2", pos: "noun", gloss: { en: "Animal", it: "Animale", de: "Tier", fr: "Animal" } },
      { iconKey: "2B3", pos: "verb", tense: "past", gloss: { en: "Search", it: "Cercare", de: "Suchen", fr: "Chercher" } },
      { iconKey: "6A1", pos: "noun", gloss: { en: "Forest", it: "Foresta", de: "Wald", fr: "Forêt" } },
      { iconKey: "7D3", pos: "adj", gloss: { en: "Good / Approved", it: "Buono", de: "Gut", fr: "Bon" } },
      { iconKey: "6D3", pos: "noun", gloss: { en: "Place / Location", it: "Posto", de: "Platz", fr: "Endroit" } },
      { iconKey: "2D2", pos: "verb", tense: "present", gloss: { en: "Sleep", it: "Dormire", de: "Schlafen", fr: "Dormir" } },
    ],
  },
  {
    id: "29",
    iconCount: 6,
    tokens: [
      tok({ order: 1, icon: ref("5D1"), frameColor: "adverb" }),
      tok({ order: 2, icon: ref("1C1"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("2D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("8D2"), frameColor: "prep" }),
      tok({ order: 5, icon: ref("4B4"), frameColor: "adj", negation: true }),
      tok({ order: 6, icon: ref("6D2"), frameColor: "noun" }),
    ],
    translations: {
      en: "In winter, you dream about the warm beach",
      it: "In inverno, sogni la spiaggia calda",
      de: "Im Winter träumst du vom warmen Strand",
      fr: "En hiver, tu rêves de la plage chaude",
    },
    glossary: [
      { iconKey: "5D1", pos: "adverb", gloss: { en: "Winter / Season", it: "Inverno", de: "Winter", fr: "Hiver" } },
      { iconKey: "1C1", pos: "noun", gloss: { en: "You", it: "Tu", de: "Du", fr: "Tu" } },
      { iconKey: "2D1", pos: "verb", tense: "present", gloss: { en: "Dream", it: "Sognare", de: "Träumen", fr: "Rêver" } },
      { iconKey: "8D2", pos: "prep", gloss: { en: "About / Of", it: "Di", de: "Von", fr: "De" } },
      { iconKey: "4B4", pos: "adj", negation: true, gloss: { en: "Warm (X = cold)", it: "Calda", de: "Warm", fr: "Chaude" } },
      { iconKey: "6D2", pos: "noun", gloss: { en: "Sea / Beach", it: "Spiaggia", de: "Strand", fr: "Plage" } },
    ],
  },
  {
    id: "30",
    iconCount: 7,
    tokens: [
      tok({ order: 1, icon: ref("7C4"), frameColor: "adj" }),
      tok({ order: 2, icon: ref("4D2"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("3C2"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("3D1"), frameColor: "verb", tense: "future" }),
      tok({ order: 5, icon: ref("7C3"), frameColor: "adj" }),
      tok({ order: 6, icon: ref("5B4"), frameColor: "adverb" }),
      tok({ order: 7, icon: ref("5A1"), frameColor: "noun" }),
    ],
    translations: {
      en: "A broken heart can become whole after time",
      it: "Un cuore spezzato può diventare intero col tempo",
      de: "Ein gebrochenes Herz kann mit der Zeit heil werden",
      fr: "Un cœur brisé peut redevenir entier avec le temps",
    },
    glossary: [
      { iconKey: "7C4", pos: "adj", gloss: { en: "Broken / Damaged", it: "Spezzato", de: "Gebrochen", fr: "Brisé" } },
      { iconKey: "4D2", pos: "noun", gloss: { en: "Heart / Body", it: "Cuore", de: "Herz", fr: "Cœur" } },
      { iconKey: "3C2", pos: "verb", tense: "present", gloss: { en: "Can", it: "Potere", de: "Können", fr: "Pouvoir" } },
      { iconKey: "3D1", pos: "verb", tense: "future", gloss: { en: "Become", it: "Diventare", de: "Werden", fr: "Devenir" } },
      { iconKey: "7C3", pos: "adj", gloss: { en: "Whole / Complete", it: "Intero", de: "Heil", fr: "Entier" } },
      { iconKey: "5B4", pos: "adverb", gloss: { en: "After", it: "Col / Dopo", de: "Mit / Nach", fr: "Avec / Après" } },
      { iconKey: "5A1", pos: "noun", gloss: { en: "Time", it: "Tempo", de: "Zeit", fr: "Temps" } },
    ],
  },
  {
    id: "31",
    iconCount: 7,
    tokens: [
      tok({ order: 1, icon: ref("1C4"), frameColor: "noun", plural: true }),
      tok({ order: 2, icon: ref("2A1"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("3B4"), frameColor: "neutral" }),
      tok({ order: 4, icon: ref("8A2"), frameColor: "prep" }),
      tok({ order: 5, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 6, icon: ref("3B3"), frameColor: "verb", tense: "present" }),
      tok({ order: 7, icon: ref("7A4"), frameColor: "adj" }),
    ],
    translations: {
      en: "The leaders think yes, but I say differently",
      it: "I leader pensano di sì, ma io dico diversamente",
      de: "Die Anführer denken ja, aber ich sage anderes",
      fr: "Les leaders pensent oui, mais je dis différemment",
    },
    glossary: [
      { iconKey: "1C4", pos: "noun", plural: true, gloss: { en: "Leaders", it: "Leader", de: "Anführer", fr: "Leaders" } },
      { iconKey: "2A1", pos: "verb", tense: "present", gloss: { en: "Think", it: "Pensare", de: "Denken", fr: "Penser" } },
      { iconKey: "3B4", pos: "neutral", gloss: { en: "Yes / Agree", it: "Sì", de: "Ja", fr: "Oui" } },
      { iconKey: "8A2", pos: "prep", gloss: { en: "But / However", it: "Ma", de: "Aber", fr: "Mais" } },
      { iconKey: "1A1", pos: "noun", gloss: { en: "I / Myself", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "3B3", pos: "verb", tense: "present", gloss: { en: "Say / Speak", it: "Dire", de: "Sagen", fr: "Dire" } },
      { iconKey: "7A4", pos: "adj", gloss: { en: "Different", it: "Diversamente", de: "Anderes", fr: "Différemment" } },
    ],
  },
  /* ============================================================
   * Additional codes #33–#52 — hand-authored by Lee + Claude, 2026-05.
   * Use the same 128-icon set as the playtest selection. Glossaries
   * follow the conjugation/case that appears in each translation.
   * ============================================================ */
  {
    id: "33",
    iconCount: 3,
    tokens: [
      tok({ order: 1, icon: ref("1A2"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2D2"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("5B1"), frameColor: "adverb" }),
    ],
    translations: {
      en: "The child sleeps at night",
      it: "Il bambino dorme di notte",
      de: "Das Kind schläft nachts",
      fr: "L'enfant dort la nuit",
    },
    glossary: [
      { iconKey: "1A2", pos: "noun", gloss: { en: "Child", it: "Bambino", de: "Kind", fr: "Enfant" } },
      { iconKey: "2D2", pos: "verb", tense: "present", gloss: { en: "Sleep", it: "Dormire", de: "Schlafen", fr: "Dormir" } },
      { iconKey: "5B1", pos: "adverb", gloss: { en: "Night", it: "Notte", de: "Nachts", fr: "Nuit" } },
    ],
  },
  {
    id: "34",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("5D4"), frameColor: "adverb" }),
      tok({ order: 3, icon: ref("2D3"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("4A2"), frameColor: "noun" }),
    ],
    translations: {
      en: "I always wish for money",
      it: "Desidero sempre dei soldi",
      de: "Ich wünsche mir immer Geld",
      fr: "Je souhaite toujours de l'argent",
    },
    glossary: [
      { iconKey: "1A1", pos: "noun", gloss: { en: "I", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "5D4", pos: "adverb", gloss: { en: "Always", it: "Sempre", de: "Immer", fr: "Toujours" } },
      { iconKey: "2D3", pos: "verb", tense: "present", gloss: { en: "Wish", it: "Desiderare", de: "Wünschen", fr: "Souhaiter" } },
      { iconKey: "4A2", pos: "noun", gloss: { en: "Money", it: "Soldi", de: "Geld", fr: "Argent" } },
    ],
  },
  {
    id: "35",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("4B2"), frameColor: "noun", plural: true }),
      tok({ order: 2, icon: ref("1D4"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("8B3"), frameColor: "prep" }),
      tok({ order: 4, icon: ref("6A1"), frameColor: "noun" }),
    ],
    translations: {
      en: "Animals run through the forest",
      it: "Gli animali corrono attraverso la foresta",
      de: "Tiere laufen durch den Wald",
      fr: "Les animaux courent à travers la forêt",
    },
    glossary: [
      { iconKey: "4B2", pos: "noun", plural: true, gloss: { en: "Animals", it: "Animali", de: "Tiere", fr: "Animaux" } },
      { iconKey: "1D4", pos: "verb", tense: "present", gloss: { en: "Run", it: "Correre", de: "Laufen", fr: "Courir" } },
      { iconKey: "8B3", pos: "prep", gloss: { en: "Through / Across", it: "Attraverso", de: "Durch", fr: "À travers" } },
      { iconKey: "6A1", pos: "noun", gloss: { en: "Forest", it: "Foresta", de: "Wald", fr: "Forêt" } },
    ],
  },
  {
    id: "36",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("1C4"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2C3"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("4A1"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("8A3"), frameColor: "prep" }),
      tok({ order: 5, icon: ref("1B4"), frameColor: "noun" }),
    ],
    translations: {
      en: "The leader gives food to the people",
      it: "Il leader dà cibo al popolo",
      de: "Der Anführer gibt dem Volk Nahrung",
      fr: "Le leader donne de la nourriture au peuple",
    },
    glossary: [
      { iconKey: "1C4", pos: "noun", gloss: { en: "Leader", it: "Leader", de: "Anführer", fr: "Leader" } },
      { iconKey: "2C3", pos: "verb", tense: "present", gloss: { en: "Give", it: "Dare", de: "Geben", fr: "Donner" } },
      { iconKey: "4A1", pos: "noun", gloss: { en: "Food", it: "Cibo", de: "Nahrung", fr: "Nourriture" } },
      { iconKey: "8A3", pos: "prep", gloss: { en: "To / Towards", it: "A / Verso", de: "Zu / An", fr: "À / Vers" } },
      { iconKey: "1B4", pos: "noun", gloss: { en: "People", it: "Popolo", de: "Volk", fr: "Peuple" } },
    ],
  },
  {
    id: "37",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2A2"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("1C3"), frameColor: "adj" }),
      tok({ order: 4, icon: ref("1B3"), frameColor: "noun" }),
    ],
    translations: {
      en: "I have a young partner",
      it: "Ho un coniuge giovane",
      de: "Ich habe einen jungen Partner",
      fr: "J'ai un conjoint jeune",
    },
    glossary: [
      { iconKey: "1A1", pos: "noun", gloss: { en: "I", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "2A2", pos: "verb", tense: "present", gloss: { en: "Have", it: "Avere", de: "Haben", fr: "Avoir" } },
      { iconKey: "1C3", pos: "adj", gloss: { en: "Young", it: "Giovane", de: "Jung", fr: "Jeune" } },
      { iconKey: "1B3", pos: "noun", gloss: { en: "Partner", it: "Coniuge", de: "Partner", fr: "Conjoint" } },
    ],
  },
  {
    id: "38",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("7B4"), frameColor: "adj" }),
      tok({ order: 2, icon: ref("4B2"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("2B1"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("4A1"), frameColor: "noun" }),
    ],
    translations: {
      en: "A big animal eats food",
      it: "Un grande animale mangia il cibo",
      de: "Ein großes Tier isst Nahrung",
      fr: "Un grand animal mange de la nourriture",
    },
    glossary: [
      { iconKey: "7B4", pos: "adj", gloss: { en: "Big / Great", it: "Grande", de: "Groß", fr: "Grand" } },
      { iconKey: "4B2", pos: "noun", gloss: { en: "Animal", it: "Animale", de: "Tier", fr: "Animal" } },
      { iconKey: "2B1", pos: "verb", tense: "present", gloss: { en: "Eat", it: "Mangiare", de: "Essen", fr: "Manger" } },
      { iconKey: "4A1", pos: "noun", gloss: { en: "Food", it: "Cibo", de: "Nahrung", fr: "Nourriture" } },
    ],
  },
  {
    id: "39",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1B2"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2B4"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("1A2"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("7C1"), frameColor: "adj" }),
    ],
    translations: {
      en: "The parent helps the worried child",
      it: "Il genitore aiuta il bambino preoccupato",
      de: "Das Elternteil hilft dem besorgten Kind",
      fr: "Le parent aide l'enfant inquiet",
    },
    glossary: [
      { iconKey: "1B2", pos: "noun", gloss: { en: "Parent", it: "Genitore", de: "Elternteil", fr: "Parent" } },
      { iconKey: "2B4", pos: "verb", tense: "present", gloss: { en: "Help", it: "Aiutare", de: "Helfen", fr: "Aider" } },
      { iconKey: "1A2", pos: "noun", gloss: { en: "Child", it: "Bambino", de: "Kind", fr: "Enfant" } },
      { iconKey: "7C1", pos: "adj", gloss: { en: "Worried", it: "Preoccupato", de: "Besorgten", fr: "Inquiet" } },
    ],
  },
  {
    id: "40",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2D3"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("3D4"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("6B1"), frameColor: "noun" }),
    ],
    translations: {
      en: "I wish to build a home",
      it: "Desidero costruire una casa",
      de: "Ich wünsche mir, ein Haus zu bauen",
      fr: "Je souhaite construire une maison",
    },
    glossary: [
      { iconKey: "1A1", pos: "noun", gloss: { en: "I", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "2D3", pos: "verb", tense: "present", gloss: { en: "Wish", it: "Desiderare", de: "Wünschen", fr: "Souhaiter" } },
      { iconKey: "3D4", pos: "verb", tense: "present", gloss: { en: "Build", it: "Costruire", de: "Bauen", fr: "Construire" } },
      { iconKey: "6B1", pos: "noun", gloss: { en: "Home", it: "Casa", de: "Haus", fr: "Maison" } },
    ],
  },
  {
    id: "41",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("4D4"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2C2"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("8B3"), frameColor: "prep" }),
      tok({ order: 4, icon: ref("6C1"), frameColor: "noun" }),
    ],
    translations: {
      en: "The car drives through the city",
      it: "La macchina guida attraverso la città",
      de: "Das Auto fährt durch die Stadt",
      fr: "La voiture roule à travers la ville",
    },
    glossary: [
      { iconKey: "4D4", pos: "noun", gloss: { en: "Car", it: "Macchina", de: "Auto", fr: "Voiture" } },
      { iconKey: "2C2", pos: "verb", tense: "present", gloss: { en: "Drive", it: "Guidare", de: "Fahren", fr: "Rouler" } },
      { iconKey: "8B3", pos: "prep", gloss: { en: "Through", it: "Attraverso", de: "Durch", fr: "À travers" } },
      { iconKey: "6C1", pos: "noun", gloss: { en: "City", it: "Città", de: "Stadt", fr: "Ville" } },
    ],
  },
  {
    id: "42",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1B2"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("5D4"), frameColor: "adverb" }),
      tok({ order: 3, icon: ref("2D2"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("6B1"), frameColor: "noun" }),
    ],
    translations: {
      en: "The grandparent always sleeps at home",
      it: "Il nonno dorme sempre a casa",
      de: "Der Großvater schläft immer zu Hause",
      fr: "Le grand-parent dort toujours à la maison",
    },
    glossary: [
      { iconKey: "1B2", pos: "noun", gloss: { en: "Grandparent", it: "Nonno", de: "Großvater", fr: "Grand-parent" } },
      { iconKey: "5D4", pos: "adverb", gloss: { en: "Always", it: "Sempre", de: "Immer", fr: "Toujours" } },
      { iconKey: "2D2", pos: "verb", tense: "present", gloss: { en: "Sleep", it: "Dormire", de: "Schlafen", fr: "Dormir" } },
      { iconKey: "6B1", pos: "noun", gloss: { en: "Home", it: "Casa", de: "Hause", fr: "Maison" } },
    ],
  },
  {
    id: "43",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("3B1"), frameColor: "noun", compoundWithNext: true }),
      tok({ order: 1, icon: ref("1A4"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("3D4"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("7B4"), frameColor: "adj" }),
      tok({ order: 4, icon: ref("3C4"), frameColor: "noun" }),
    ],
    translations: {
      en: "The artist builds a great work",
      it: "L'artista costruisce una grande opera",
      de: "Der Künstler schafft ein großes Werk",
      fr: "L'artiste construit une grande œuvre",
    },
    glossary: [
      { iconKey: "3B1", pos: "noun", compound: { en: "Artist (Art+Person)", it: "Artista (Arte+Persona)", de: "Künstler (Kunst+Person)", fr: "Artiste (Art+Personne)" }, gloss: { en: "Art", it: "Arte", de: "Kunst", fr: "Art" } },
      { iconKey: "1A4", pos: "noun", compound: { en: "Artist (Art+Person)", it: "Artista (Arte+Persona)", de: "Künstler (Kunst+Person)", fr: "Artiste (Art+Personne)" }, gloss: { en: "Person", it: "Persona", de: "Person", fr: "Personne" } },
      { iconKey: "3D4", pos: "verb", tense: "present", gloss: { en: "Build / Make", it: "Costruire", de: "Schaffen", fr: "Construire" } },
      { iconKey: "7B4", pos: "adj", gloss: { en: "Great", it: "Grande", de: "Großes", fr: "Grande" } },
      { iconKey: "3C4", pos: "noun", gloss: { en: "Work", it: "Opera / Lavoro", de: "Werk", fr: "Œuvre" } },
    ],
  },
  {
    id: "44",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1C4"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2D3"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("3D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("6C1"), frameColor: "noun" }),
    ],
    translations: {
      en: "The leader wishes to change the city",
      it: "Il leader desidera cambiare la città",
      de: "Der Anführer wünscht, die Stadt zu verändern",
      fr: "Le leader souhaite changer la ville",
    },
    glossary: [
      { iconKey: "1C4", pos: "noun", gloss: { en: "Leader", it: "Leader", de: "Anführer", fr: "Leader" } },
      { iconKey: "2D3", pos: "verb", tense: "present", gloss: { en: "Wish", it: "Desiderare", de: "Wünschen", fr: "Souhaiter" } },
      { iconKey: "3D1", pos: "verb", tense: "present", gloss: { en: "Change", it: "Cambiare", de: "Verändern", fr: "Changer" } },
      { iconKey: "6C1", pos: "noun", gloss: { en: "City", it: "Città", de: "Stadt", fr: "Ville" } },
    ],
  },
  {
    id: "45",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("3B1"), frameColor: "noun", compoundWithNext: true }),
      tok({ order: 1, icon: ref("1A4"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("3D2"), frameColor: "verb", tense: "past" }),
      tok({ order: 3, icon: ref("8D2"), frameColor: "prep" }),
      tok({ order: 4, icon: ref("6A3"), frameColor: "noun" }),
    ],
    translations: {
      en: "A scientist asked about the world",
      it: "Uno scienziato ha chiesto del mondo",
      de: "Ein Wissenschaftler hat nach der Welt gefragt",
      fr: "Un scientifique a demandé au sujet du monde",
    },
    glossary: [
      { iconKey: "3B1", pos: "noun", compound: { en: "Scientist (Science+Person)", it: "Scienziato (Scienza+Persona)", de: "Wissenschaftler (Wissenschaft+Person)", fr: "Scientifique (Science+Personne)" }, gloss: { en: "Science", it: "Scienza", de: "Wissenschaft", fr: "Science" } },
      { iconKey: "1A4", pos: "noun", compound: { en: "Scientist (Science+Person)", it: "Scienziato (Scienza+Persona)", de: "Wissenschaftler (Wissenschaft+Person)", fr: "Scientifique (Science+Personne)" }, gloss: { en: "Person", it: "Persona", de: "Person", fr: "Personne" } },
      { iconKey: "3D2", pos: "verb", tense: "past", gloss: { en: "Ask", it: "Chiedere", de: "Gefragt", fr: "Demander" } },
      { iconKey: "8D2", pos: "prep", gloss: { en: "About", it: "Di / Riguardo", de: "Nach / Über", fr: "Au sujet de" } },
      { iconKey: "6A3", pos: "noun", gloss: { en: "World", it: "Mondo", de: "Welt", fr: "Monde" } },
    ],
  },
  {
    id: "46",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1D3"), frameColor: "noun", plural: true }),
      tok({ order: 2, icon: ref("3C4"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("7A4"), frameColor: "adj" }),
      tok({ order: 4, icon: ref("6B1"), frameColor: "noun" }),
    ],
    translations: {
      en: "Foreigners search for a different home",
      it: "Gli stranieri cercano una casa diversa",
      de: "Ausländer suchen ein anderes Zuhause",
      fr: "Les étrangers cherchent une maison différente",
    },
    glossary: [
      { iconKey: "1D3", pos: "noun", plural: true, gloss: { en: "Foreigners", it: "Stranieri", de: "Ausländer", fr: "Étrangers" } },
      { iconKey: "3C4", pos: "verb", tense: "present", gloss: { en: "Search", it: "Cercare", de: "Suchen", fr: "Chercher" } },
      { iconKey: "7A4", pos: "adj", gloss: { en: "Different", it: "Diversa", de: "Anderes", fr: "Différente" } },
      { iconKey: "6B1", pos: "noun", gloss: { en: "Home", it: "Casa", de: "Zuhause", fr: "Maison" } },
    ],
  },
  {
    id: "47",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("8D2"), frameColor: "prep" }),
      tok({ order: 4, icon: ref("7B2"), frameColor: "adj" }),
      tok({ order: 5, icon: ref("6A3"), frameColor: "noun" }),
    ],
    translations: {
      en: "I dream about a peaceful world",
      it: "Sogno un mondo pacifico",
      de: "Ich träume von einer friedlichen Welt",
      fr: "Je rêve d'un monde paisible",
    },
    glossary: [
      { iconKey: "1A1", pos: "noun", gloss: { en: "I", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "2D1", pos: "verb", tense: "present", gloss: { en: "Dream", it: "Sognare", de: "Träumen", fr: "Rêver" } },
      { iconKey: "8D2", pos: "prep", gloss: { en: "About / Of", it: "Di", de: "Von", fr: "De" } },
      { iconKey: "7B2", pos: "adj", gloss: { en: "Peaceful", it: "Pacifico", de: "Friedlichen", fr: "Paisible" } },
      { iconKey: "6A3", pos: "noun", gloss: { en: "World", it: "Mondo", de: "Welt", fr: "Monde" } },
    ],
  },
  {
    id: "48",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1C3"), frameColor: "adj" }),
      tok({ order: 2, icon: ref("1D3"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("3C2"), frameColor: "verb", tense: "present", negation: true }),
      tok({ order: 4, icon: ref("3B3"), frameColor: "verb", tense: "present" }),
    ],
    translations: {
      en: "The young foreigner cannot speak",
      it: "Il giovane straniero non può parlare",
      de: "Der junge Ausländer kann nicht sprechen",
      fr: "Le jeune étranger ne peut pas parler",
    },
    glossary: [
      { iconKey: "1C3", pos: "adj", gloss: { en: "Young", it: "Giovane", de: "Junge", fr: "Jeune" } },
      { iconKey: "1D3", pos: "noun", gloss: { en: "Foreigner", it: "Straniero", de: "Ausländer", fr: "Étranger" } },
      { iconKey: "3C2", pos: "verb", tense: "present", negation: true, gloss: { en: "Cannot", it: "Non può", de: "Kann nicht", fr: "Ne peut pas" } },
      { iconKey: "3B3", pos: "verb", tense: "present", gloss: { en: "Speak", it: "Parlare", de: "Sprechen", fr: "Parler" } },
    ],
  },
  {
    id: "49",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("5B3"), frameColor: "adverb" }),
      tok({ order: 2, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("3D1"), frameColor: "verb", tense: "future" }),
      tok({ order: 4, icon: ref("1C4"), frameColor: "noun" }),
    ],
    translations: {
      en: "Tomorrow I will become a leader",
      it: "Domani diventerò un leader",
      de: "Morgen werde ich ein Anführer",
      fr: "Demain je deviendrai un leader",
    },
    glossary: [
      { iconKey: "5B3", pos: "adverb", gloss: { en: "Tomorrow", it: "Domani", de: "Morgen", fr: "Demain" } },
      { iconKey: "1A1", pos: "noun", gloss: { en: "I", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "3D1", pos: "verb", tense: "future", gloss: { en: "Become", it: "Diventare", de: "Werden", fr: "Devenir" } },
      { iconKey: "1C4", pos: "noun", gloss: { en: "Leader", it: "Leader", de: "Anführer", fr: "Leader" } },
    ],
  },
  {
    id: "50",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("7D1"), frameColor: "noun", plural: true }),
      tok({ order: 2, icon: ref("3A4"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("8C1"), frameColor: "adverb" }),
      tok({ order: 4, icon: ref("6D1"), frameColor: "noun" }),
    ],
    translations: {
      en: "Children play more in school",
      it: "I bambini giocano di più a scuola",
      de: "Kinder spielen mehr in der Schule",
      fr: "Les enfants jouent plus à l'école",
    },
    glossary: [
      { iconKey: "7D1", pos: "noun", plural: true, gloss: { en: "Children", it: "Bambini", de: "Kinder", fr: "Enfants" } },
      { iconKey: "3A4", pos: "verb", tense: "present", gloss: { en: "Play", it: "Giocare", de: "Spielen", fr: "Jouer" } },
      { iconKey: "8C1", pos: "adverb", gloss: { en: "More", it: "Di più", de: "Mehr", fr: "Plus" } },
      { iconKey: "6D1", pos: "noun", gloss: { en: "School", it: "Scuola", de: "Schule", fr: "École" } },
    ],
  },
  {
    id: "51",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2A1"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("6A3"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("3D1"), frameColor: "verb", tense: "present" }),
    ],
    translations: {
      en: "I think the world is changing",
      it: "Penso che il mondo stia cambiando",
      de: "Ich denke, die Welt verändert sich",
      fr: "Je pense que le monde change",
    },
    glossary: [
      { iconKey: "1A1", pos: "noun", gloss: { en: "I", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "2A1", pos: "verb", tense: "present", gloss: { en: "Think", it: "Pensare", de: "Denken", fr: "Penser" } },
      { iconKey: "6A3", pos: "noun", gloss: { en: "World", it: "Mondo", de: "Welt", fr: "Monde" } },
      { iconKey: "3D1", pos: "verb", tense: "present", gloss: { en: "Change", it: "Cambiare", de: "Verändert", fr: "Changer" } },
    ],
  },
  {
    id: "52",
    iconCount: 6,
    tokens: [
      tok({ order: 1, icon: ref("1C3"), frameColor: "adj" }),
      tok({ order: 2, icon: ref("3B1"), frameColor: "noun", compoundWithNext: true }),
      tok({ order: 2, icon: ref("1A4"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("2D3"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("3D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 5, icon: ref("6A3"), frameColor: "noun" }),
    ],
    translations: {
      en: "The young scientist wishes to change the world",
      it: "Il giovane scienziato desidera cambiare il mondo",
      de: "Der junge Wissenschaftler wünscht, die Welt zu verändern",
      fr: "Le jeune scientifique souhaite changer le monde",
    },
    glossary: [
      { iconKey: "1C3", pos: "adj", gloss: { en: "Young", it: "Giovane", de: "Junge", fr: "Jeune" } },
      { iconKey: "3B1", pos: "noun", compound: { en: "Scientist (Science+Person)", it: "Scienziato (Scienza+Persona)", de: "Wissenschaftler (Wissenschaft+Person)", fr: "Scientifique (Science+Personne)" }, gloss: { en: "Science", it: "Scienza", de: "Wissenschaft", fr: "Science" } },
      { iconKey: "1A4", pos: "noun", compound: { en: "Scientist (Science+Person)", it: "Scienziato (Scienza+Persona)", de: "Wissenschaftler (Wissenschaft+Person)", fr: "Scientifique (Science+Personne)" }, gloss: { en: "Person", it: "Persona", de: "Person", fr: "Personne" } },
      { iconKey: "2D3", pos: "verb", tense: "present", gloss: { en: "Wish", it: "Desiderare", de: "Wünschen", fr: "Souhaiter" } },
      { iconKey: "3D1", pos: "verb", tense: "present", gloss: { en: "Change", it: "Cambiare", de: "Verändern", fr: "Changer" } },
      { iconKey: "6A3", pos: "noun", gloss: { en: "World", it: "Mondo", de: "Welt", fr: "Monde" } },
    ],
  },
  /* ============================================================
   * Codes #53–#72 — second hand-authored batch. Uses more of the
   * underused icons (sea, mountain, path, whole, almost, which,
   * cold/warm, broken heart, ugly clothes) and adds question forms,
   * future-negation, copula-via-equals, and more compounds.
   * ============================================================ */
  {
    id: "53",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("7A4"), frameColor: "adj" }),
      tok({ order: 2, icon: ref("1A4"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("2A2"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("7C4"), frameColor: "adj" }),
      tok({ order: 5, icon: ref("4D2"), frameColor: "noun" }),
    ],
    translations: {
      en: "A different person has a broken heart",
      it: "Una persona diversa ha un cuore spezzato",
      de: "Eine andere Person hat ein gebrochenes Herz",
      fr: "Une personne différente a un cœur brisé",
    },
    glossary: [
      { iconKey: "7A4", pos: "adj", gloss: { en: "Different", it: "Diversa", de: "Andere", fr: "Différente" } },
      { iconKey: "1A4", pos: "noun", gloss: { en: "Person", it: "Persona", de: "Person", fr: "Personne" } },
      { iconKey: "2A2", pos: "verb", tense: "present", gloss: { en: "Have", it: "Avere", de: "Haben", fr: "Avoir" } },
      { iconKey: "7C4", pos: "adj", gloss: { en: "Broken", it: "Spezzato", de: "Gebrochenes", fr: "Brisé" } },
      { iconKey: "4D2", pos: "noun", gloss: { en: "Heart", it: "Cuore", de: "Herz", fr: "Cœur" } },
    ],
  },
  {
    id: "54",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("7A2"), frameColor: "adj" }),
      tok({ order: 2, icon: ref("5D1"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("3D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("4B4"), frameColor: "adj" }),
      tok({ order: 5, icon: ref("6D2"), frameColor: "noun" }),
    ],
    translations: {
      en: "The cold winter changes the warm sea",
      it: "L'inverno freddo cambia il mare caldo",
      de: "Der kalte Winter verändert das warme Meer",
      fr: "L'hiver froid change la mer chaude",
    },
    glossary: [
      { iconKey: "7A2", pos: "adj", gloss: { en: "Cold", it: "Freddo", de: "Kalte", fr: "Froid" } },
      { iconKey: "5D1", pos: "noun", gloss: { en: "Winter", it: "Inverno", de: "Winter", fr: "Hiver" } },
      { iconKey: "3D1", pos: "verb", tense: "present", gloss: { en: "Change", it: "Cambiare", de: "Verändern", fr: "Changer" } },
      { iconKey: "4B4", pos: "adj", gloss: { en: "Warm", it: "Caldo", de: "Warme", fr: "Chaude" } },
      { iconKey: "6D2", pos: "noun", gloss: { en: "Sea", it: "Mare", de: "Meer", fr: "Mer" } },
    ],
  },
  {
    id: "55",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("6A2"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("8B3"), frameColor: "prep" }),
      tok({ order: 3, icon: ref("6B2"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("8D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 5, icon: ref("7D4"), frameColor: "adj" }),
    ],
    translations: {
      en: "The path through the mountain is long",
      it: "La strada attraverso la montagna è lunga",
      de: "Der Weg durch den Berg ist lang",
      fr: "Le chemin à travers la montagne est long",
    },
    glossary: [
      { iconKey: "6A2", pos: "noun", gloss: { en: "Path / Road", it: "Strada", de: "Weg", fr: "Chemin" } },
      { iconKey: "8B3", pos: "prep", gloss: { en: "Through", it: "Attraverso", de: "Durch", fr: "À travers" } },
      { iconKey: "6B2", pos: "noun", gloss: { en: "Mountain", it: "Montagna", de: "Berg", fr: "Montagne" } },
      { iconKey: "8D1", pos: "verb", tense: "present", gloss: { en: "Is / Equals", it: "È / Uguale", de: "Ist / Gleich", fr: "Est / Égal" } },
      { iconKey: "7D4", pos: "adj", gloss: { en: "Long", it: "Lunga", de: "Lang", fr: "Long" } },
    ],
  },
  {
    id: "56",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("7D1"), frameColor: "noun", plural: true }),
      tok({ order: 2, icon: ref("2D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("8D2"), frameColor: "prep" }),
      tok({ order: 4, icon: ref("6C2"), frameColor: "noun" }),
    ],
    translations: {
      en: "Children dream of space",
      it: "I bambini sognano lo spazio",
      de: "Kinder träumen vom Weltraum",
      fr: "Les enfants rêvent de l'espace",
    },
    glossary: [
      { iconKey: "7D1", pos: "noun", plural: true, gloss: { en: "Children", it: "Bambini", de: "Kinder", fr: "Enfants" } },
      { iconKey: "2D1", pos: "verb", tense: "present", gloss: { en: "Dream", it: "Sognare", de: "Träumen", fr: "Rêver" } },
      { iconKey: "8D2", pos: "prep", gloss: { en: "Of", it: "Di / Lo", de: "Vom", fr: "De" } },
      { iconKey: "6C2", pos: "noun", gloss: { en: "Space", it: "Spazio", de: "Weltraum", fr: "Espace" } },
    ],
  },
  {
    id: "57",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("8D2"), frameColor: "prep" }),
      tok({ order: 4, icon: ref("7A4"), frameColor: "adj" }),
      tok({ order: 5, icon: ref("6D3"), frameColor: "noun" }),
    ],
    translations: {
      en: "I dream of a different place",
      it: "Sogno un luogo diverso",
      de: "Ich träume von einem anderen Ort",
      fr: "Je rêve d'un endroit différent",
    },
    glossary: [
      { iconKey: "1A1", pos: "noun", gloss: { en: "I", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "2D1", pos: "verb", tense: "present", gloss: { en: "Dream", it: "Sognare", de: "Träumen", fr: "Rêver" } },
      { iconKey: "8D2", pos: "prep", gloss: { en: "Of", it: "Di / Un", de: "Von", fr: "De" } },
      { iconKey: "7A4", pos: "adj", gloss: { en: "Different", it: "Diverso", de: "Anderen", fr: "Différent" } },
      { iconKey: "6D3", pos: "noun", gloss: { en: "Place", it: "Luogo", de: "Ort", fr: "Endroit" } },
    ],
  },
  {
    id: "58",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("5B4"), frameColor: "prep" }),
      tok({ order: 2, icon: ref("5D1"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("6A3"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("3D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 5, icon: ref("4B4"), frameColor: "adj" }),
    ],
    translations: {
      en: "After winter the world becomes warm",
      it: "Dopo l'inverno il mondo diventa caldo",
      de: "Nach dem Winter wird die Welt warm",
      fr: "Après l'hiver le monde devient chaud",
    },
    glossary: [
      { iconKey: "5B4", pos: "prep", gloss: { en: "After", it: "Dopo", de: "Nach", fr: "Après" } },
      { iconKey: "5D1", pos: "noun", gloss: { en: "Winter", it: "Inverno", de: "Winter", fr: "Hiver" } },
      { iconKey: "6A3", pos: "noun", gloss: { en: "World", it: "Mondo", de: "Welt", fr: "Monde" } },
      { iconKey: "3D1", pos: "verb", tense: "present", gloss: { en: "Become", it: "Diventare", de: "Wird", fr: "Devenir" } },
      { iconKey: "4B4", pos: "adj", gloss: { en: "Warm", it: "Caldo", de: "Warm", fr: "Chaud" } },
    ],
  },
  {
    id: "59",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1B2"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2C3"), frameColor: "verb", tense: "past" }),
      tok({ order: 3, icon: ref("7C2"), frameColor: "adj" }),
      tok({ order: 4, icon: ref("4C4"), frameColor: "noun" }),
    ],
    translations: {
      en: "The grandparent gave ugly clothes",
      it: "Il nonno ha dato dei vestiti brutti",
      de: "Der Großvater gab hässliche Kleidung",
      fr: "Le grand-parent a donné des vêtements laids",
    },
    glossary: [
      { iconKey: "1B2", pos: "noun", gloss: { en: "Grandparent", it: "Nonno", de: "Großvater", fr: "Grand-parent" } },
      { iconKey: "2C3", pos: "verb", tense: "past", gloss: { en: "Give", it: "Dare", de: "Geben", fr: "Donner" } },
      { iconKey: "7C2", pos: "adj", gloss: { en: "Ugly", it: "Brutti", de: "Hässliche", fr: "Laids" } },
      { iconKey: "4C4", pos: "noun", gloss: { en: "Clothes", it: "Vestiti", de: "Kleidung", fr: "Vêtements" } },
    ],
  },
  {
    id: "60",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("8A4"), frameColor: "adverb" }),
      tok({ order: 3, icon: ref("5D4"), frameColor: "adverb" }),
      tok({ order: 4, icon: ref("7A1"), frameColor: "adj" }),
    ],
    translations: {
      en: "I am almost always happy",
      it: "Sono quasi sempre felice",
      de: "Ich bin fast immer glücklich",
      fr: "Je suis presque toujours heureux",
    },
    glossary: [
      { iconKey: "1A1", pos: "noun", gloss: { en: "I", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "8A4", pos: "adverb", gloss: { en: "Almost", it: "Quasi", de: "Fast", fr: "Presque" } },
      { iconKey: "5D4", pos: "adverb", gloss: { en: "Always", it: "Sempre", de: "Immer", fr: "Toujours" } },
      { iconKey: "7A1", pos: "adj", gloss: { en: "Happy", it: "Felice", de: "Glücklich", fr: "Heureux" } },
    ],
  },
  {
    id: "61",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("7C3"), frameColor: "adj" }),
      tok({ order: 2, icon: ref("6A3"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("8D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("6B1"), frameColor: "noun" }),
    ],
    translations: {
      en: "The whole world equals home",
      it: "Tutto il mondo è la casa",
      de: "Die ganze Welt ist Heimat",
      fr: "Le monde entier est la maison",
    },
    glossary: [
      { iconKey: "7C3", pos: "adj", gloss: { en: "Whole / Entire", it: "Tutto", de: "Ganze", fr: "Entier" } },
      { iconKey: "6A3", pos: "noun", gloss: { en: "World", it: "Mondo", de: "Welt", fr: "Monde" } },
      { iconKey: "8D1", pos: "verb", tense: "present", gloss: { en: "Equals / Is", it: "È", de: "Ist", fr: "Est" } },
      { iconKey: "6B1", pos: "noun", gloss: { en: "Home", it: "Casa", de: "Heimat", fr: "Maison" } },
    ],
  },
  {
    id: "62",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("5B4"), frameColor: "prep" }),
      tok({ order: 2, icon: ref("5A1"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("4D2"), frameColor: "noun", plural: true }),
      tok({ order: 4, icon: ref("3D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 5, icon: ref("7C3"), frameColor: "adj" }),
    ],
    translations: {
      en: "After time, hearts become whole",
      it: "Dopo il tempo, i cuori diventano interi",
      de: "Mit der Zeit werden Herzen ganz",
      fr: "Avec le temps, les cœurs deviennent entiers",
    },
    glossary: [
      { iconKey: "5B4", pos: "prep", gloss: { en: "After / With", it: "Dopo", de: "Mit", fr: "Avec" } },
      { iconKey: "5A1", pos: "noun", gloss: { en: "Time", it: "Tempo", de: "Zeit", fr: "Temps" } },
      { iconKey: "4D2", pos: "noun", plural: true, gloss: { en: "Hearts", it: "Cuori", de: "Herzen", fr: "Cœurs" } },
      { iconKey: "3D1", pos: "verb", tense: "present", gloss: { en: "Become", it: "Diventare", de: "Werden", fr: "Devenir" } },
      { iconKey: "7C3", pos: "adj", gloss: { en: "Whole", it: "Interi", de: "Ganz", fr: "Entiers" } },
    ],
  },
  {
    id: "63",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("1C1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2C2"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("8A3"), frameColor: "prep" }),
      tok({ order: 4, icon: ref("6D2"), frameColor: "noun" }),
      tok({ order: 5, icon: ref("5C2"), frameColor: "adverb" }),
    ],
    translations: {
      en: "You drive to the sea every year",
      it: "Guidi al mare ogni anno",
      de: "Du fährst jedes Jahr zum Meer",
      fr: "Tu conduis à la mer chaque année",
    },
    glossary: [
      { iconKey: "1C1", pos: "noun", gloss: { en: "You", it: "Tu", de: "Du", fr: "Tu" } },
      { iconKey: "2C2", pos: "verb", tense: "present", gloss: { en: "Drive", it: "Guidare", de: "Fahren", fr: "Conduire" } },
      { iconKey: "8A3", pos: "prep", gloss: { en: "To", it: "A", de: "Zum", fr: "À" } },
      { iconKey: "6D2", pos: "noun", gloss: { en: "Sea", it: "Mare", de: "Meer", fr: "Mer" } },
      { iconKey: "5C2", pos: "adverb", gloss: { en: "Every year", it: "Ogni anno", de: "Jedes Jahr", fr: "Chaque année" } },
    ],
  },
  {
    id: "64",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("7D3"), frameColor: "adj" }),
      tok({ order: 2, icon: ref("1A3"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("2C3"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("4B1"), frameColor: "noun" }),
    ],
    translations: {
      en: "The good friend gives a phone",
      it: "Il buon amico dà un telefono",
      de: "Der gute Freund gibt ein Telefon",
      fr: "Le bon ami donne un téléphone",
    },
    glossary: [
      { iconKey: "7D3", pos: "adj", gloss: { en: "Good", it: "Buon", de: "Gute", fr: "Bon" } },
      { iconKey: "1A3", pos: "noun", gloss: { en: "Friend", it: "Amico", de: "Freund", fr: "Ami" } },
      { iconKey: "2C3", pos: "verb", tense: "present", gloss: { en: "Give", it: "Dare", de: "Geben", fr: "Donner" } },
      { iconKey: "4B1", pos: "noun", gloss: { en: "Phone", it: "Telefono", de: "Telefon", fr: "Téléphone" } },
    ],
  },
  {
    id: "65",
    iconCount: 5,
    tokens: [
      tok({ order: 1, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("5D4"), frameColor: "adverb" }),
      tok({ order: 3, icon: ref("2D3"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("7B2"), frameColor: "adj" }),
      tok({ order: 5, icon: ref("5A1"), frameColor: "noun" }),
    ],
    translations: {
      en: "I always wish for peaceful time",
      it: "Desidero sempre un tempo pacifico",
      de: "Ich wünsche mir immer friedliche Zeit",
      fr: "Je souhaite toujours un temps paisible",
    },
    glossary: [
      { iconKey: "1A1", pos: "noun", gloss: { en: "I", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "5D4", pos: "adverb", gloss: { en: "Always", it: "Sempre", de: "Immer", fr: "Toujours" } },
      { iconKey: "2D3", pos: "verb", tense: "present", gloss: { en: "Wish", it: "Desiderare", de: "Wünschen", fr: "Souhaiter" } },
      { iconKey: "7B2", pos: "adj", gloss: { en: "Peaceful", it: "Pacifico", de: "Friedliche", fr: "Paisible" } },
      { iconKey: "5A1", pos: "noun", gloss: { en: "Time", it: "Tempo", de: "Zeit", fr: "Temps" } },
    ],
  },
  {
    id: "66",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("3B4"), frameColor: "neutral" }),
      tok({ order: 2, icon: ref("8A2"), frameColor: "prep" }),
      tok({ order: 3, icon: ref("7D1"), frameColor: "noun", plural: true }),
      tok({ order: 4, icon: ref("2D2"), frameColor: "verb", tense: "present" }),
    ],
    translations: {
      en: "Yes, but children sleep",
      it: "Sì, ma i bambini dormono",
      de: "Ja, aber Kinder schlafen",
      fr: "Oui, mais les enfants dorment",
    },
    glossary: [
      { iconKey: "3B4", pos: "neutral", gloss: { en: "Yes", it: "Sì", de: "Ja", fr: "Oui" } },
      { iconKey: "8A2", pos: "prep", gloss: { en: "But", it: "Ma", de: "Aber", fr: "Mais" } },
      { iconKey: "7D1", pos: "noun", plural: true, gloss: { en: "Children", it: "Bambini", de: "Kinder", fr: "Enfants" } },
      { iconKey: "2D2", pos: "verb", tense: "present", gloss: { en: "Sleep", it: "Dormire", de: "Schlafen", fr: "Dormir" } },
    ],
  },
  {
    id: "67",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2B1"), frameColor: "verb", tense: "future", negation: true }),
      tok({ order: 3, icon: ref("4A1"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("5B3"), frameColor: "adverb" }),
    ],
    translations: {
      en: "I will not eat food tomorrow",
      it: "Non mangerò cibo domani",
      de: "Ich werde morgen kein Essen essen",
      fr: "Je ne mangerai pas de nourriture demain",
    },
    glossary: [
      { iconKey: "1A1", pos: "noun", gloss: { en: "I", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "2B1", pos: "verb", tense: "future", negation: true, gloss: { en: "Will not eat", it: "Non mangerò", de: "Werde nicht essen", fr: "Ne mangerai pas" } },
      { iconKey: "4A1", pos: "noun", gloss: { en: "Food", it: "Cibo", de: "Essen", fr: "Nourriture" } },
      { iconKey: "5B3", pos: "adverb", gloss: { en: "Tomorrow", it: "Domani", de: "Morgen", fr: "Demain" } },
    ],
  },
  {
    id: "68",
    iconCount: 6,
    tokens: [
      tok({ order: 1, icon: ref("8A4"), frameColor: "adverb" }),
      tok({ order: 2, icon: ref("5A2"), frameColor: "adverb" }),
      tok({ order: 3, icon: ref("1A1"), frameColor: "noun" }),
      tok({ order: 4, icon: ref("2D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 5, icon: ref("8D2"), frameColor: "prep" }),
      tok({ order: 6, icon: ref("1C1"), frameColor: "noun" }),
    ],
    translations: {
      en: "Almost every week I dream of you",
      it: "Quasi ogni settimana sogno di te",
      de: "Fast jede Woche träume ich von dir",
      fr: "Presque chaque semaine je rêve de toi",
    },
    glossary: [
      { iconKey: "8A4", pos: "adverb", gloss: { en: "Almost", it: "Quasi", de: "Fast", fr: "Presque" } },
      { iconKey: "5A2", pos: "adverb", gloss: { en: "Every week", it: "Ogni settimana", de: "Jede Woche", fr: "Chaque semaine" } },
      { iconKey: "1A1", pos: "noun", gloss: { en: "I", it: "Io", de: "Ich", fr: "Je" } },
      { iconKey: "2D1", pos: "verb", tense: "present", gloss: { en: "Dream", it: "Sognare", de: "Träume", fr: "Rêver" } },
      { iconKey: "8D2", pos: "prep", gloss: { en: "Of", it: "Di", de: "Von", fr: "De" } },
      { iconKey: "1C1", pos: "noun", gloss: { en: "You", it: "Te", de: "Dir", fr: "Toi" } },
    ],
  },
  {
    id: "69",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("8B4"), frameColor: "neutral" }),
      tok({ order: 2, icon: ref("1A2"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("8D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("7A1"), frameColor: "adj" }),
    ],
    translations: {
      en: "Which child is happy?",
      it: "Quale bambino è felice?",
      de: "Welches Kind ist glücklich?",
      fr: "Quel enfant est heureux ?",
    },
    glossary: [
      { iconKey: "8B4", pos: "neutral", gloss: { en: "Which", it: "Quale", de: "Welches", fr: "Quel" } },
      { iconKey: "1A2", pos: "noun", gloss: { en: "Child", it: "Bambino", de: "Kind", fr: "Enfant" } },
      { iconKey: "8D1", pos: "verb", tense: "present", gloss: { en: "Is / Equals", it: "È", de: "Ist", fr: "Est" } },
      { iconKey: "7A1", pos: "adj", gloss: { en: "Happy", it: "Felice", de: "Glücklich", fr: "Heureux" } },
    ],
  },
  {
    id: "70",
    iconCount: 6,
    tokens: [
      tok({ order: 1, icon: ref("7C1"), frameColor: "adj" }),
      tok({ order: 2, icon: ref("3B1"), frameColor: "noun", compoundWithNext: true }),
      tok({ order: 2, icon: ref("1A4"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("2D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("8D2"), frameColor: "prep" }),
      tok({ order: 5, icon: ref("6C2"), frameColor: "noun" }),
    ],
    translations: {
      en: "A worried scientist dreams of space",
      it: "Uno scienziato preoccupato sogna lo spazio",
      de: "Ein besorgter Wissenschaftler träumt vom Weltraum",
      fr: "Un scientifique inquiet rêve de l'espace",
    },
    glossary: [
      { iconKey: "7C1", pos: "adj", gloss: { en: "Worried", it: "Preoccupato", de: "Besorgter", fr: "Inquiet" } },
      { iconKey: "3B1", pos: "noun", compound: { en: "Scientist (Science+Person)", it: "Scienziato (Scienza+Persona)", de: "Wissenschaftler (Wissenschaft+Person)", fr: "Scientifique (Science+Personne)" }, gloss: { en: "Science", it: "Scienza", de: "Wissenschaft", fr: "Science" } },
      { iconKey: "1A4", pos: "noun", compound: { en: "Scientist (Science+Person)", it: "Scienziato (Scienza+Persona)", de: "Wissenschaftler (Wissenschaft+Person)", fr: "Scientifique (Science+Personne)" }, gloss: { en: "Person", it: "Persona", de: "Person", fr: "Personne" } },
      { iconKey: "2D1", pos: "verb", tense: "present", gloss: { en: "Dream", it: "Sognare", de: "Träumt", fr: "Rêver" } },
      { iconKey: "8D2", pos: "prep", gloss: { en: "Of", it: "Lo / Di", de: "Vom", fr: "De" } },
      { iconKey: "6C2", pos: "noun", gloss: { en: "Space", it: "Spazio", de: "Weltraum", fr: "Espace" } },
    ],
  },
  {
    id: "71",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("7A1"), frameColor: "adj" }),
      tok({ order: 2, icon: ref("1A3"), frameColor: "noun", plural: true }),
      tok({ order: 3, icon: ref("3A4"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("5C4"), frameColor: "adverb" }),
    ],
    translations: {
      en: "Happy friends play often",
      it: "Gli amici felici giocano spesso",
      de: "Glückliche Freunde spielen oft",
      fr: "Des amis heureux jouent souvent",
    },
    glossary: [
      { iconKey: "7A1", pos: "adj", gloss: { en: "Happy", it: "Felici", de: "Glückliche", fr: "Heureux" } },
      { iconKey: "1A3", pos: "noun", plural: true, gloss: { en: "Friends", it: "Amici", de: "Freunde", fr: "Amis" } },
      { iconKey: "3A4", pos: "verb", tense: "present", gloss: { en: "Play", it: "Giocare", de: "Spielen", fr: "Jouer" } },
      { iconKey: "5C4", pos: "adverb", gloss: { en: "Often", it: "Spesso", de: "Oft", fr: "Souvent" } },
    ],
  },
  {
    id: "72",
    iconCount: 4,
    tokens: [
      tok({ order: 1, icon: ref("1C3"), frameColor: "adj" }),
      tok({ order: 2, icon: ref("1C4"), frameColor: "noun" }),
      tok({ order: 3, icon: ref("3D1"), frameColor: "verb", tense: "present" }),
      tok({ order: 4, icon: ref("6D1"), frameColor: "noun" }),
    ],
    translations: {
      en: "The young leader changes the school",
      it: "Il giovane leader cambia la scuola",
      de: "Der junge Anführer verändert die Schule",
      fr: "Le jeune leader change l'école",
    },
    glossary: [
      { iconKey: "1C3", pos: "adj", gloss: { en: "Young", it: "Giovane", de: "Junge", fr: "Jeune" } },
      { iconKey: "1C4", pos: "noun", gloss: { en: "Leader", it: "Leader", de: "Anführer", fr: "Leader" } },
      { iconKey: "3D1", pos: "verb", tense: "present", gloss: { en: "Change", it: "Cambiare", de: "Verändern", fr: "Changer" } },
      { iconKey: "6D1", pos: "noun", gloss: { en: "School", it: "Scuola", de: "Schule", fr: "École" } },
    ],
  },
  {
    id: "32",
    iconCount: 8,
    tokens: [
      tok({ order: 1, icon: ref("1B2"), frameColor: "noun" }),
      tok({ order: 2, icon: ref("2A1"), frameColor: "verb", tense: "present" }),
      tok({ order: 3, icon: ref("8D2"), frameColor: "prep" }),
      tok({ order: 4, icon: ref("8B4"), frameColor: "neutral" }),
      tok({ order: 5, icon: ref("1A2"), frameColor: "noun" }),
      tok({ order: 6, icon: ref("3D1"), frameColor: "verb", tense: "future" }),
      tok({ order: 7, icon: ref("1C4"), frameColor: "noun" }),
      tok({ order: 8, icon: ref("5B3"), frameColor: "adverb" }),
    ],
    translations: {
      en: "A parent thinks about which child will become the leader tomorrow",
      it: "Un genitore pensa a quale figlio diventerà il leader domani",
      de: "Ein Elternteil denkt daran, welches Kind morgen zum Anführer wird",
      fr: "Un parent pense à quel enfant deviendra le leader demain",
    },
    glossary: [
      { iconKey: "1B2", pos: "noun", gloss: { en: "Parent", it: "Genitore", de: "Elternteil", fr: "Parent" } },
      { iconKey: "2A1", pos: "verb", tense: "present", gloss: { en: "Think", it: "Pensare", de: "Denken", fr: "Penser" } },
      { iconKey: "8D2", pos: "prep", gloss: { en: "About / Which", it: "A quale", de: "Daran / Welches", fr: "À" } },
      { iconKey: "8B4", pos: "neutral", gloss: { en: "Which / What kind", it: "Quale", de: "Welches", fr: "Quel" } },
      { iconKey: "1A2", pos: "noun", gloss: { en: "Child", it: "Figlio", de: "Kind", fr: "Enfant" } },
      { iconKey: "3D1", pos: "verb", tense: "future", gloss: { en: "Become", it: "Diventerà", de: "Wird", fr: "Deviendra" } },
      { iconKey: "1C4", pos: "noun", gloss: { en: "Leader", it: "Leader", de: "Anführer", fr: "Leader" } },
      { iconKey: "5B3", pos: "adverb", gloss: { en: "Tomorrow", it: "Domani", de: "Morgen", fr: "Demain" } },
    ],
  },
];

export function getCode(id: string): CodeSentence | undefined {
  return CODES.find((c) => c.id === id);
}

export function getRandomCode(opts?: { maxIcons?: number }): CodeSentence {
  const pool = opts?.maxIcons ? CODES.filter((c) => c.iconCount <= opts.maxIcons!) : CODES;
  return pool[Math.floor(Math.random() * pool.length)];
}
